import { getProviderId, getProviderLabel } from "../store/helper";

// Contact fields that must be present (non-empty) before a lab-panel order can
// be placed/finalized. Keys match `personalInfoAnswers` and the
// GetCustomerProfile response.
export const BASIC_INFO_REQUIRED_FIELDS = [
  "phone",
  "countryCode",
  "city",
  "address",
  "zip",
] as const;

// Lab-panel orders (Quest via Junction) can only be shipped/processed for
// addresses in these countries. `countryCode` is the ISO2 code stored on the
// profile (see StyledPhoneInput / parseAddressComponents).
export const LAB_ORDER_ALLOWED_COUNTRY_CODES = ["US"] as const;

const isEmpty = (value: any): boolean =>
  value === null || value === undefined || String(value).trim() === "";

// Returns the required fields that are empty in the given source (personal-info
// answers or a client profile). Empty array = all present.
export const getMissingBasicInfoFields = (source: any): string[] => {
  if (!source) return [...BASIC_INFO_REQUIRED_FIELDS];
  return BASIC_INFO_REQUIRED_FIELDS.filter((field) => isEmpty(source[field]));
};

export const isBasicInfoComplete = (source: any): boolean =>
  getMissingBasicInfoFields(source).length === 0;

// True when the profile's country is one lab-panel orders can be placed in.
// An empty country is reported by `getMissingBasicInfoFields` instead, so it
// is treated as allowed here to avoid double-reporting.
export const isLabOrderCountry = (source: any): boolean => {
  const code = source?.countryCode;
  if (isEmpty(code)) return true;
  return (LAB_ORDER_ALLOWED_COUNTRY_CODES as readonly string[]).includes(
    String(code).trim().toUpperCase()
  );
};

// US states (profile `stateCode`) where a lab can't take orders through
// Junction's physician network, keyed by `enLabID` (Quest = 7, LabCorp = 5).
// Per Junction (Oct 2026): NY and NJ only go through BioReference, which we
// don't integrate; RI has no lab at all; Quest has paused draws in HI.
export const LAB_ORDER_BLOCKED_STATES: Record<number, readonly string[]> = {
  7: ["NY", "NJ", "RI", "HI"],
  5: ["NY", "NJ", "RI"],
};

// Panels without a provider are Quest's, the platform default.
const DEFAULT_LAB_ID = 7;

// Providers (slug, enLabID or numeric string, as panels carry them) that can't
// take an order for the profile's state. Empty = every provider is fine.
export const getBlockedLabProviders = (
  source: any,
  providers: Array<string | number | null | undefined>
): number[] => {
  const state = String(source?.stateCode ?? "").trim().toUpperCase();
  if (!state) return [];
  const ids = providers.map((p) => getProviderId(p) ?? DEFAULT_LAB_ID);
  return Array.from(new Set(ids)).filter((id) =>
    (LAB_ORDER_BLOCKED_STATES[id] ?? []).includes(state)
  );
};

// Banner copy for a state no selected lab can serve, e.g. "Quest Diagnostics".
export const getBlockedStateMessage = (
  source: any,
  blockedIds: number[]
): string => {
  const labs = blockedIds.map((id) => getProviderLabel(id)).join(" and ");
  const state = source?.stateName || source?.stateCode;
  return `${labs} can't take lab orders for addresses in ${state}. Please update the address to continue.`;
};

// Single gate for lab-panel flows: contact info must be complete, the address
// must be in an allowed country, and in a state every panel's lab serves.
export const isBasicInfoValidForLabOrder = (
  source: any,
  providers: Array<string | number | null | undefined> = []
): boolean =>
  isBasicInfoComplete(source) &&
  isLabOrderCountry(source) &&
  getBlockedLabProviders(source, providers).length === 0;
